import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';
import type { Property, RenderVariant, RenderSettings } from '../../types/property';
import { supabase } from '../../lib/supabase-client';
import { projectId, publicAnonKey } from '../../../utils/supabase/info';

interface SelectedPhotoData {
  roomName: string;
  variants: RenderVariant[];
  originalUrl?: string;
  renderSettings?: RenderSettings;
  originalGcsPath?: string;
  generationId?: string;
}

interface UseRenderPreviewActionsParams {
  selectedProperty: Property | null;
  setSelectedProperty: (p: Property | null) => void;
  selectedPhotoData: SelectedPhotoData | null;
  setSelectedPhotoData: (data: SelectedPhotoData | null) => void;
  properties: Property[];
  setProperties: React.Dispatch<React.SetStateAction<Property[]>>;
  setIsRenderPreviewOpen: (open: boolean) => void;
  setIsRenderWizardOpen: (open: boolean) => void;
  setRenderWizardState: (state: {
    mode: 'create' | 'rerender';
    initialImageUrl?: string;
    initialImageId?: string;
    initialRoomName?: string;
    initialGcsPath?: string;
  }) => void;
  handleDeletePhotoFromGallery: (propertyId: string, photoUrl: string) => void;
  handleToggleFavorite: (photoUrl: string) => Promise<void>;
  handleMovePhoto: (photoUrl: string, targetId: string, roomName: string) => void;
  handleAssignUnsortedPhoto?: (targetId: string, photoData: any) => Promise<void>;
  createProperty: (name: string) => Promise<string | null>;
  fetchProperties: () => void;
}

export function useRenderPreviewActions({
  selectedProperty,
  setSelectedProperty,
  selectedPhotoData,
  setSelectedPhotoData,
  properties,
  setProperties,
  setIsRenderPreviewOpen,
  setIsRenderWizardOpen,
  setRenderWizardState,
  handleDeletePhotoFromGallery,
  handleToggleFavorite,
  handleMovePhoto,
  handleAssignUnsortedPhoto,
  createProperty,
  fetchProperties,
}: UseRenderPreviewActionsParams) {

  const [isRetrying, setIsRetrying] = useState(false);

  const handleDelete = useCallback(() => {
    if (selectedPhotoData?.originalUrl && selectedProperty) {
      handleDeletePhotoFromGallery(selectedProperty.id, selectedPhotoData.originalUrl);
      setIsRenderPreviewOpen(false);
    }
  }, [selectedPhotoData, selectedProperty, handleDeletePhotoFromGallery, setIsRenderPreviewOpen]);

  const handleToggleFav = useCallback(() => {
    if (selectedPhotoData) {
      const mainImg = selectedProperty?.galleryImages?.find(img => img.roomName === selectedPhotoData.roomName);
      if (mainImg) {
        handleToggleFavorite(mainImg.url);
      }
    }
  }, [selectedPhotoData, selectedProperty, handleToggleFavorite]);

  const handleMove = useCallback((targetId: string) => {
    if (selectedPhotoData) {
      if (!selectedProperty && handleAssignUnsortedPhoto) {
        handleAssignUnsortedPhoto(targetId, selectedPhotoData);
      } else {
        handleMovePhoto(selectedPhotoData.originalUrl!, targetId, selectedPhotoData.roomName);
      }
      setIsRenderPreviewOpen(false);
      setSelectedProperty(null);
    }
  }, [selectedPhotoData, selectedProperty, handleAssignUnsortedPhoto, handleMovePhoto, setIsRenderPreviewOpen, setSelectedProperty]);

  const handleAutoSaveAndClose = useCallback(async () => {
    if (selectedPhotoData && handleAssignUnsortedPhoto && !selectedProperty) {
      const now = new Date();
      const timestampName = `${now.getDate().toString().padStart(2, '0')}-${(now.getMonth() + 1).toString().padStart(2, '0')}-${now.getFullYear()}-${now.getHours().toString().padStart(2, '0')}-${now.getMinutes().toString().padStart(2, '0')}`;

      const newPropId = await createProperty(timestampName);
      if (newPropId) {
        await handleAssignUnsortedPhoto(newPropId, selectedPhotoData);
        await fetchProperties();
        setIsRenderPreviewOpen(false);
        setSelectedProperty(null);
        toast.success(`Utworzono nieruchomość: ${timestampName}`);
      } else {
        toast.error("Błąd automatycznego zapisu.");
      }
    }
  }, [selectedPhotoData, selectedProperty, handleAssignUnsortedPhoto, createProperty, fetchProperties, setIsRenderPreviewOpen, setSelectedProperty]);

  const handleRerender = useCallback(() => {
    if (selectedPhotoData) {
      const image = selectedProperty?.galleryImages?.find(
        (img) => img.roomName === selectedPhotoData.roomName && img.url === selectedPhotoData.originalUrl
      );
      const imageId = image?.id;

      setRenderWizardState({
        mode: 'rerender',
        initialImageUrl: selectedPhotoData.originalUrl,
        initialImageId: imageId,
        initialRoomName: selectedPhotoData.roomName,
        initialGcsPath: selectedPhotoData.originalGcsPath,
      });

      setIsRenderPreviewOpen(false);

      setTimeout(() => {
        setIsRenderWizardOpen(true);
      }, 100);
    }
  }, [selectedPhotoData, selectedProperty, setRenderWizardState, setIsRenderPreviewOpen, setIsRenderWizardOpen]);

  const isFavorite = useMemo(() => {
    return selectedProperty?.galleryImages?.find(img => img.roomName === selectedPhotoData?.roomName)?.favorite || false;
  }, [selectedProperty, selectedPhotoData]);

  const handleRetryVariant = useCallback(async () => {
    if (isRetrying || !selectedPhotoData) return;

    setIsRetrying(true);
    try {
      // Find generationId: from selectedPhotoData, gallery image, or DB lookup
      let genId = selectedPhotoData.generationId;

      if (!genId && selectedProperty) {
        const galleryImage = selectedProperty.galleryImages?.find(
          img => img.roomName === selectedPhotoData.roomName &&
                 (img.originalUrl === selectedPhotoData.originalUrl || img.url === selectedPhotoData.originalUrl)
        );
        genId = galleryImage?.generationId;
      }

      if (!genId && selectedPhotoData.variants?.length > 0) {
        // Look up from generation_outputs by variant ID
        const variantId = selectedPhotoData.variants[0].id;
        const { data: output } = await supabase
          .from('generation_outputs')
          .select('generation_id')
          .or(`output_image_id.eq.${variantId},id.eq.${variantId}`)
          .limit(1)
          .single();
        genId = output?.generation_id;
      }

      if (!genId) {
        toast.error('Nie znaleziono generacji do ponowienia');
        setIsRetrying(false);
        return;
      }

      // Cache the found generationId for future use
      if (!selectedPhotoData.generationId) {
        setSelectedPhotoData({ ...selectedPhotoData, generationId: genId });
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setIsRetrying(false);
        return;
      }

      const supabaseUrl = `https://${projectId}.supabase.co`;
      const response = await fetch(`${supabaseUrl}/functions/v1/generate-render/retry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': publicAnonKey,
        },
        body: JSON.stringify({ generationId: genId }),
      });

      const result = await response.json();
      if (!result.success) {
        toast.error(result.error || 'Nie udało się ponowić generacji');
        setIsRetrying(false);
        return;
      }

      toast.loading('Generowanie brakującego wariantu...', { id: 'retry-variant' });

      // Poll for the new completed variant
      const pollForResult = async () => {
        const currentVariantCount = selectedPhotoData?.variants?.length || 0;

        for (let i = 0; i < 36; i++) {
          await new Promise(r => setTimeout(r, 5000));

          const { data: outputs } = await supabase
            .from('generation_outputs')
            .select('id, variant_number, status, gcs_path, signed_url, ai_provider, output_image_id')
            .eq('generation_id', genId)
            .eq('status', 'completed')
            .order('variant_number');

          if (outputs && outputs.length > currentVariantCount) {
            const newOutput = outputs[outputs.length - 1];
            const newVariant: RenderVariant = {
              id: newOutput.output_image_id || newOutput.id,
              url: newOutput.signed_url || '',
              label: `Wariant ${String.fromCharCode(65 + currentVariantCount)}`,
              gcsPath: newOutput.gcs_path || undefined,
              source: newOutput.ai_provider as RenderVariant['source'],
            };

            // Update selectedPhotoData with new variant
            setSelectedPhotoData({
              ...selectedPhotoData!,
              variants: [...(selectedPhotoData!.variants || []), newVariant],
            });

            // Update gallery images in properties
            if (selectedProperty) {
              setProperties((prev) => {
                const propIdx = prev.findIndex(p => p.id === selectedProperty.id);
                if (propIdx === -1) return prev;

                const updated = [...prev];
                const prop = { ...updated[propIdx] };
                const images = [...(prop.galleryImages || [])];
                const imgIdx = images.findIndex(img => img.generationId === genId);

                if (imgIdx !== -1) {
                  const img = { ...images[imgIdx] };
                  img.variants = [...(img.variants || []), newVariant];
                  img.variantCount = img.variants.length;
                  img.url = newVariant.url || img.url;
                  images[imgIdx] = img;
                  prop.galleryImages = images;
                  updated[propIdx] = prop;
                  setSelectedProperty(prop);
                }

                return updated;
              });
            }

            toast.dismiss('retry-variant');
            toast.success('Wygenerowano brakujący wariant!');
            return;
          }

          // Check if generation failed entirely
          const { data: gen } = await supabase
            .from('generations')
            .select('status')
            .eq('id', genId)
            .single();

          if (gen?.status === 'failed' || gen?.status === 'cancelled') {
            toast.dismiss('retry-variant');
            toast.error('Generacja wariantu nie powiodła się.');
            return;
          }
        }

        // Timeout after 180s
        toast.dismiss('retry-variant');
        toast.error('Generacja trwa zbyt długo. Spróbuj ponownie.');
      };

      pollForResult().finally(() => setIsRetrying(false));
    } catch (err) {
      console.error('Retry error:', err);
      toast.error('Błąd ponowienia generacji');
      setIsRetrying(false);
    }
  }, [selectedPhotoData, selectedProperty, isRetrying, setSelectedPhotoData, setProperties, setSelectedProperty]);

  const availableProperties = useMemo(() => {
    return properties.map(p => ({
      id: p.id,
      address: p.address,
      photoCount: p.photoCount,
      imageUrl: p.imageUrl,
    }));
  }, [properties]);

  return {
    handleDelete,
    handleToggleFav,
    handleMove,
    handleAutoSaveAndClose,
    handleRerender,
    handleRetryVariant,
    isRetrying,
    isFavorite,
    availableProperties,
  };
}
